import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { setPrompt, setDuration } from 'store/slices/podcastGen';
import { generatePrompt } from 'features/podcast/services/generatePrompt';

// zod schema
const podcastFormSchema = z.object({
  prompt: z
    .string()
    .min(5, { message: 'Topic should be of atleast 5 characters' })
    .max(500, { message: 'Topic must be less than 500 characters' }),
  duration: z
    .number()
    .min(1, { message: 'Duration must be at least 1 minute' })
    .max(10, { message: 'Duration must be less than 10 minutes' }),
});

interface PodcastGeneratorFormProps {
  onAssignVoice: () => void;
}

export function PodcastGeneratorForm({
  onAssignVoice,
}: PodcastGeneratorFormProps) {
  // to get the states
  const dispatch = useDispatch();

  const [localPrompt, setLocalPrompt] = useState<string>('');
  const [localDuration, setLocalDuration] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // to manage errors in inputs
  const [errors, setErrors] = useState<{ prompt?: string; duration?: string }>({
    prompt: '',
    duration: '',
  });

  // validation check
  const handleFormSubmit = () => {
    try {
      // Validate form data
      const parsedData = podcastFormSchema.parse({
        prompt: localPrompt,
        duration: localDuration,
      });
      dispatch(setPrompt({ prompt: parsedData.prompt }));
      dispatch(setDuration({ duration: parsedData.duration }));
      setErrors({});
      onAssignVoice();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formErrors: Record<string, string> = {};
        error.errors.forEach((e) => {
          if (e.path[0]) {
            formErrors[e.path[0] as string] = e.message;
          }
        });
        setErrors(formErrors);
      }
    }
  };

  // to handle scroll on number input
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement === inputRef.current) {
        e.preventDefault();
      }
    };

    const currentInput = inputRef.current;
    if (currentInput) {
      currentInput.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (currentInput) {
        currentInput.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  const getPrompt = async () => {
    setLoading(true);

    try {
      const response = await generatePrompt.getPrompt();

      setLocalPrompt(response as unknown as string);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-h-screen">
      <div className="flex text-text-primary w-full lg:w-[60%] mb-4 justify-between items-center">
        <div className="mr-[20px]">
          <div className="text-[14px] lg:text-sm uppercase tracking-wider font-medium text-text-primary">
            Step 1
          </div>
          <div className="text-xl lg:text-2xl font-bold text-nowrap mt-1">
            Enter a Topic
          </div>
        </div>
        <div>
          <div className="text-[14px] lg:text-sm uppercase tracking-wider font-medium text-gray-400">
            Step 2
          </div>
          <div className="text-xl lg:text-2xl font-normal text-nowrap mt-1">
            Assign Characters
          </div>
        </div>
      </div>
      <Card className="p-6 lg:p-10 bg-background text-text-primary space-y-6 w-full lg:w-full mt-2 shadow-lg">
        <div>
          <label className="text-[15px] font-semibold mb-3 block text-text-secondary">
            Enter Topic
          </label>
          <textarea
            className={`h-[150px] m-h-[150px] text-[16px] w-full rounded-lg border border-stroke-regular bg-background px-4 py-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-[17px] file:font-medium file:text-foreground placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-[17px] resize-none focus:ring-2 focus:ring-primary/20 transition-all ${errors.prompt && 'border-red-600'}`}
            placeholder="What do you want the podcast to be about?"
            value={localPrompt}
            onChange={(e) => setLocalPrompt(e.target.value)}
          />
          <div className="flex items-center">
            <p className="text-[13px] w-[134px] lg:w-auto font-medium text-gray-500">
              UNSURE OF WHAT YOU WANT TO CREATE?
            </p>
            <button
              type="button"
              className="ml-auto text-primary font-medium cursor-pointer underline underline-offset-4 disabled:text-gray-400 disabled:cursor-not-allowed hover:text-primary/80 transition-colors"
              onClick={() => getPrompt()}
              disabled={loading}
            >
              {loading ? (
                <span>Generating...</span>
              ) : (
                <span>Generate Topic</span>
              )}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[15px] font-semibold mb-3 block text-text-secondary">
            Length
          </label>
          <Input
            type="number"
            ref={inputRef}
            className={`focus:outline-none focus:ring-2 focus:ring-primary/20 text-[15px] h-12 transition-all ${errors.duration && 'border-red-500'}`}
            placeholder="Duration (in minutes)"
            min={1}
            max={20}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setLocalDuration(value);
            }}
            required
          />
        </div>
        {errors.prompt ? (
          <p className="text-red-500 text-sm font-medium mt-1 h-[5px]">
            {errors.prompt}
          </p>
        ) : errors.duration ? (
          <p className="text-red-500 text-sm font-medium mt-1 h-[5px]">
            {errors.duration}
          </p>
        ) : (
          <div />
        )}
        <div onClick={handleFormSubmit}>
          <Button
            className="w-full bg-primary text-white h-12 text-[16px] font-medium hover:bg-primary/90 transition-colors"
            variant={'customPrimary'}
          >
            Assign Voices
          </Button>
        </div>
      </Card>
    </div>
  );
}
